import { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { Dropdown, DropdownChangeEvent } from 'primereact/dropdown';
import ActiveFilters, { ActiveFilterChip } from '../../components/novarium/ActiveFilters';
import MoreFiltersToggle from '../../components/novarium/MoreFiltersToggle';
import PeriodField from '../../components/novarium/PeriodField';
import { VIRTUAL_PANEL_CLASS, VIRTUAL_SCROLL } from '../../components/novarium/virtualOptions';
import { NewBornOptionsDTO } from '../../dto/newborn/NewBornDTO';
import {
    NEWBORN_MORE_FILTERS,
    NewBornFilterField,
    NewBornSelectFilters,
    NO_SELECT_FILTERS,
} from './newBornFilterModel';
import { NewBornFiltersData } from './useNewBornFilters';

/** Valor de la opción «Todos»: el combo no admite `null` como valor seleccionado. */
const ALL = 'all';

const MORE_FILTERS_ID = 'newborn-more-filters';

interface SelectOption {
    value: string;
    label: string;
}

/** Un filtro de «Más filtros»: su combo (valores de texto, `ALL` es «Todos») y cómo se lee y se escribe en los filtros. */
interface FieldSpec {
    field: NewBornFilterField;
    label: string;
    options: SelectOption[];
    /** Ancho del campo: S (228) o M (307, nombres largos). */
    wide?: boolean;
    /** Lista larga: con buscador y scroll virtual. */
    long?: boolean;
    /** El filtro elegido en forma de texto de combo; `ALL` si no hay. */
    read: (filters: NewBornSelectFilters) => string;
    /** El patch de filtros de un valor del combo. */
    write: (value: string) => Partial<NewBornSelectFilters>;
}

interface NewBornFiltersProps {
    data: NewBornFiltersData;
    /** Opciones de los combos; `null` mientras no llegaron. */
    options: NewBornOptionsDTO | null;
}

const asText = (value: string | number | boolean | null): string => (value === null ? ALL : String(value));
const idOf = (value: string): number | null => (value === ALL ? null : Number(value));

/**
 * Filtros de los nacimientos, todos al instante (no hay «Consultar»). A la vista, el «Período» (un selector con las fechas
 * solo en «Personalizado») y «Más filtros»; debajo, los chips con lo aplicado y «Quitar todos».
 */
export default function NewBornFilters({ data, options }: NewBornFiltersProps) {
    const intl = useIntl();
    const { filters, errors, period, rangeText, changeFilter, patchFilters, changeDate, commitDates, changePeriod } = data;

    const optionsReady = options !== null;
    const all = intl.formatMessage({ id: 'NewBornFilterAll' });

    const specs = useMemo<FieldSpec[]>(() => {
        const withAll = (list: SelectOption[]): SelectOption[] => [{ value: ALL, label: all }, ...list];
        const byId = (list: { id: number; name: string }[] | undefined): SelectOption[] =>
            withAll((list ?? []).map((option) => ({ value: String(option.id), label: option.name })));
        const byCode = (list: { code: string; name: string }[] | undefined): SelectOption[] =>
            withAll((list ?? []).map((option) => ({ value: option.code, label: option.name })));
        const yesNo = withAll([
            { value: 'true', label: intl.formatMessage({ id: 'NewBornFilterYes' }) },
            { value: 'false', label: intl.formatMessage({ id: 'NewBornFilterNo' }) },
        ]);
        const flag = (value: string): boolean | null => (value === ALL ? null : value === 'true');
        const label = (id: string) => intl.formatMessage({ id });
        return [
            {
                field: 'serviceId', label: label('NewBornFilterService'), options: byId(options?.services), wide: true,
                read: (f) => asText(f.serviceId), write: (v) => ({ serviceId: idOf(v) }),
            },
            {
                field: 'deliveryType', label: label('NewBornFilterDelivery'), options: byCode(options?.deliveryTypes),
                read: (f) => asText(f.deliveryType),
                write: (v) => ({ deliveryType: v === ALL ? null : v as NewBornSelectFilters['deliveryType'] }),
            },
            {
                field: 'birthState', label: label('NewBornFilterBirthState'), options: byCode(options?.birthStates),
                read: (f) => asText(f.birthState),
                write: (v) => ({ birthState: v === ALL ? null : v as NewBornSelectFilters['birthState'] }),
            },
            {
                field: 'sexId', label: label('NewBornFilterSex'), options: byId(options?.sexes),
                read: (f) => asText(f.sexId), write: (v) => ({ sexId: idOf(v) }),
            },
            {
                field: 'alert', label: label('NewBornFilterAlert'), options: yesNo,
                read: (f) => asText(f.alert), write: (v) => ({ alert: flag(v) }),
            },
            {
                field: 'needsReview', label: label('NewBornFilterReview'), options: yesNo,
                read: (f) => asText(f.needsReview), write: (v) => ({ needsReview: flag(v) }),
            },
            {
                field: 'placementId', label: label('NewBornFilterPlacement'), options: byId(options?.placements), wide: true,
                read: (f) => asText(f.placementId), write: (v) => ({ placementId: idOf(v) }),
            },
            {
                field: 'financierId', label: label('NewBornFilterFinancier'), options: byId(options?.financiers), wide: true, long: true,
                read: (f) => asText(f.financierId), write: (v) => ({ financierId: idOf(v) }),
            },
        ];
    }, [options, intl, all]);

    // «Servicio» no se ofrece si el sitio tiene uno solo (salvo que ya esté elegido, para poder quitarlo)
    const visibleSpecs = useMemo(
        () => specs.filter((spec) => spec.field !== 'serviceId' || !optionsReady || (options?.services.length ?? 0) > 1
            || filters.serviceId !== null),
        [specs, optionsReady, options, filters.serviceId],
    );

    const dateErrors = useMemo(() => ({
        from: errors.from === undefined ? undefined : intl.formatMessage({ id: 'NewBornDateRequiredError' }),
        to: errors.to === undefined
            ? undefined
            : intl.formatMessage({ id: errors.to === 'order' ? 'NewBornDateOrderError' : 'NewBornDateRequiredError' }),
    }), [errors.from, errors.to, intl]);

    const moreActive = NEWBORN_MORE_FILTERS.filter((field) => filters[field] !== null).length;
    // «Más filtros» se abre solo cuando pasa a haber alguno aplicado (también si lo activa una nota de los indicadores); después se puede cerrar
    const [moreOpen, setMoreOpen] = useState(moreActive > 0);
    useEffect(() => {
        if (moreActive > 0) setMoreOpen(true);
    }, [moreActive]);

    /** Un chip por filtro elegido, «Filtro: valor»; las opciones todavía sin cargar se nombran por su valor. */
    const chips = useMemo<ActiveFilterChip<NewBornFilterField>[]>(() => specs.flatMap((spec) => {
        const value = spec.read(filters);
        if (value === ALL) return [];
        const name = spec.options.find((option) => option.value === value)?.label
            ?? intl.formatMessage({ id: 'NewBornChipUnknown' }, { id: value });
        return [{ key: spec.field, label: intl.formatMessage({ id: 'NewBornChip' }, { filter: spec.label, name }) }];
    }), [specs, filters, intl]);

    const onSelect = (spec: FieldSpec) => (event: DropdownChangeEvent) => changeFilter(spec.write(event.value as string));

    return (
        <section className="nv-section" role="search" aria-label={intl.formatMessage({ id: 'NewBornFilters' })}>
            <div className="nv-form-row">
                <PeriodField
                    idPrefix="newborn"
                    period={period}
                    rangeText={rangeText}
                    from={filters.from}
                    to={filters.to}
                    errors={dateErrors}
                    onDateChange={changeDate}
                    onDatesCommit={commitDates}
                    onPeriodChange={changePeriod}
                />
                <MoreFiltersToggle
                    open={moreOpen}
                    active={moreActive}
                    controls={MORE_FILTERS_ID}
                    onToggle={() => setMoreOpen((open) => !open)}
                />
            </div>

            {moreOpen && (
                <div id={MORE_FILTERS_ID} className="nv-form-row">
                    {visibleSpecs.map((spec) => {
                        const inputId = `newborn-${spec.field}`;
                        return (
                            <div key={spec.field} className={`nv-field ${spec.wide ? 'nv-w-m' : 'nv-w-s'}`}>
                                <label htmlFor={inputId}>{spec.label}</label>
                                <Dropdown
                                    inputId={inputId}
                                    value={spec.read(filters)}
                                    options={spec.options}
                                    optionLabel="label"
                                    optionValue="value"
                                    disabled={!optionsReady}
                                    {...(spec.long
                                        ? {
                                            filter: true,
                                            filterBy: 'label',
                                            virtualScrollerOptions: VIRTUAL_SCROLL,
                                            panelClassName: `nv-scope ${VIRTUAL_PANEL_CLASS}`,
                                            emptyFilterMessage: intl.formatMessage({ id: 'NewBornFilterNoMatches' }),
                                        }
                                        : { panelClassName: 'nv-scope' })}
                                    onChange={onSelect(spec)}
                                />
                            </div>
                        );
                    })}
                </div>
            )}

            <ActiveFilters
                chips={chips}
                onRemove={(field) => patchFilters({ [field]: null })}
                onClear={() => patchFilters(NO_SELECT_FILTERS)}
            />
        </section>
    );
}
